import { createSelector, weakMapMemoize } from "@reduxjs/toolkit";

import { differenceInMinutes, differenceInHours } from "date-fns";

import {
  OrganizationProject,
  OrganizationProjectStatusesTypes
} from "Api/organizations/projects/OrganizationProjects.types";
import { SUBSCRIPTION_PLAN } from "Constants/constants";
import { getCategory } from "Reducers/integration";
// Deep import, not the `Reducers/projectCreation` barrel: the barrel pulls in
// thunks that reach back into `Reducers/organization`, and this module is on
// that path. The types module is a leaf.
import { isGitProvider } from "Reducers/projectCreation/projectCreation.types";

import type { ProvisioningPlanStep } from "Api/organizations/projects/streamProvisioning.request";
import type { GitProvider } from "Reducers/projectCreation/projectCreation.types";
import type { RootState } from "Store/configureStore";

import { isStageVisible } from "./provisioningStages";

import type { ProvisioningStep } from "./types";

export const organizationProjectsStateSelector = (state: RootState) =>
  state.organizationProjects;

export const organizationProjectsSelector = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (organizationProjects, { organizationId }) =>
    Object.values(organizationProjects?.data?.[organizationId] ?? {})
);

export const isOrganizationProvisioningProject = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (state, { organizationId }) =>
    state.organizationsProvisioningProject.find(id => id === organizationId)
);

export const loadingOrganizationProjectsSelector = createSelector(
  organizationProjectsStateSelector,
  organizationProjectsState => organizationProjectsState.loading
);

export const organizationProjectSelector = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationId: string; projectId: string }) =>
    params,
  (state, { organizationId, projectId }) =>
    state.data?.[organizationId]?.[projectId]
);

// Plan dropdown options derived from the project's plan titles, ordered by the
// canonical SUBSCRIPTION_PLAN sequence (unknown plans last). Mirrors the
// legacy subscriptionPlanOptionsSelector now that the plan form sources its
// data from the OrganizationProject.
export const organizationProjectPlanOptionsSelector = createSelector(
  organizationProjectSelector,
  project => {
    const planTitles = project?.project_options?.plan_titles ?? {};
    return Object.keys(planTitles)
      .sort((prev, next) => {
        const prevIndex = SUBSCRIPTION_PLAN.indexOf(prev);
        const nextIndex = SUBSCRIPTION_PLAN.indexOf(next);
        return (
          (prevIndex === -1 ? SUBSCRIPTION_PLAN.length : prevIndex) -
          (nextIndex === -1 ? SUBSCRIPTION_PLAN.length : nextIndex)
        );
      })
      .map(value => ({ value, label: planTitles[value] }));
  }
);

/**
 * Whether the project carries a dedicated tag (DG2/DG3). Replaces the legacy
 * `subscriptionDedicatedSelector`. The projects endpoint OMITS `dedicated_tag`
 * when there is none (it does not send `""`), so this is a presence check —
 * never compare against `""`.
 */
export const organizationProjectDedicatedSelector = createSelector(
  organizationProjectSelector,
  project => Boolean(project?.dedicated_tag)
);

export const selectProjectProvisioningSlot = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationUuid: string; projectId: string }) =>
    params,
  (state, { organizationUuid, projectId }) =>
    state.provisioning?.[organizationUuid]?.[projectId]
);

export const selectStreamConnectionStatus = (
  state: RootState,
  params: { organizationUuid: string }
) => state.organizationProjects.streamConnections[params.organizationUuid];

export const selectIsPendingGithubIntegration = (
  state: RootState,
  params: { projectId: string }
) =>
  Boolean(
    state.organizationProjects.pendingGithubIntegrations[params.projectId]
  );

export const selectHasSeenIntegrationActivities = (
  state: RootState,
  params: { projectId: string }
) =>
  Boolean(
    state.organizationProjects.seenIntegrationActivities[params.projectId]
  );

// Plain selector (not memoized): returns a primitive that input-stable
// `createSelector` chains can depend on. `integrationsSelector` returns a
// fresh sorted array each call, so consumers can't use it as a reselect input.
// `getCategory` keeps THIRD_PARTIES as the single source of truth for what
// counts as a git integration.
export const selectHasGitIntegration = (
  state: RootState,
  params: { projectId: string }
): boolean => {
  const integrations = state.integration?.data?.[params.projectId] ?? {};
  return Object.values(integrations).some(
    integration => !!integration && getCategory(integration) === "git"
  );
};

/**
 * The git provider the in-project wizard should open on: the `?connect=` param
 * the create flow appends to the project URL, falling back to the choice the
 * create flow recorded for this project.
 *
 * Plain function for the same reason as `selectHasGitIntegration` above: it
 * returns a primitive, and a `createSelector` keyed on a params object would
 * thrash its single-entry cache for no gain.
 *
 * Returns null when the wizard should ask instead of assume — no param and no
 * recorded choice, an unrecognised param, or a project that already has a git
 * integration, in which case there is nothing left to connect.
 */
export const selectWizardGitProvider = (
  state: RootState,
  params: { projectId: string; connect: string | null }
): GitProvider => {
  // Checked before either carrier, since it disqualifies both.
  if (selectHasGitIntegration(state, { projectId: params.projectId }))
    return null;

  if (isGitProvider(params.connect)) return params.connect;

  // No param. Either the user never came from the create page, or they did and
  // then navigated away and back — every in-app route to a project builds a
  // clean URL. `wizardGitProviders` tells the two apart; it is only ever
  // written for a project this browser created.
  return (
    state.organizationProjects?.wizardGitProviders?.[params.projectId] ?? null
  );
};

export const buildSteps = (
  plan: ReadonlyArray<ProvisioningPlanStep>,
  seen: ReadonlyArray<ProvisioningStep>,
  hasFailed: boolean
): ProvisioningStep[] => {
  const seenByStage = new Map(seen.map(step => [step.stage, step]));
  const knownStages = new Set(plan.map(s => s.stage));
  const latestKnownIndex = seen.reduce((max, step) => {
    const idx = plan.findIndex(s => s.stage === step.stage);
    return idx > max ? idx : max;
  }, -1);

  const merged: ProvisioningStep[] = plan.map(({ stage, label }, index) => {
    const seenStep = seenByStage.get(stage);
    if (seenStep) return seenStep;
    // Stages before the latest one we saw are inferred as done — covers the
    // case where the client connected mid-stream and missed earlier events.
    if (index < latestKnownIndex) return { stage, label, status: "done" };
    return { stage, label, status: "pending" };
  });

  const unknownSeen = seen.filter(step => !knownStages.has(step.stage));
  const withUnknown = [...merged, ...unknownSeen];

  // Drop stages the console deliberately hides (see provisioningStages.ts).
  // Filtering before the failure pass means failure propagation lands on the
  // nearest *visible* row when the failing stage itself is hidden.
  const visible = withUnknown.filter(step => isStageVisible(step.stage));

  if (!hasFailed) return visible;

  const indexToFail = (() => {
    for (let i = visible.length - 1; i >= 0; i--) {
      if (visible[i].status !== "pending") return i;
    }
    return -1;
  })();

  if (indexToFail < 0) return visible;
  if (visible[indexToFail].status === "failed") return visible;

  return visible.map((step, index) => {
    if (index === indexToFail) return { ...step, status: "failed" };
    if (index < indexToFail && step.status === "in_progress") {
      return { ...step, status: "done" };
    }
    return step;
  });
};

export type ProvisioningTimeline = {
  steps: ProvisioningStep[];
  totalSteps: number;
  currentStep: ProvisioningStep | undefined;
  currentIndex: number;
  failedIndex: number;
  failureReason: string | undefined;
  isFailure: boolean;
  isConnectionLost: boolean;
  hasAnyEvent: boolean;
};

// `weakMapMemoize` keeps a per-arg cache so N cards rendering side-by-side
// don't thrash each other's memo entry (the default size-1 cache would).
export const selectProvisioningTimeline = createSelector(
  [
    selectProjectProvisioningSlot,
    selectStreamConnectionStatus,
    (
      _state: RootState,
      params: {
        organizationUuid: string;
        projectId: string;
        hasFailed?: boolean;
      }
    ) => Boolean(params.hasFailed)
  ],
  (slot, streamStatus, hasFailedArg): ProvisioningTimeline => {
    const isFailure = hasFailedArg || slot?.streamStatus === "error";
    // Connection loss only matters while we're still waiting — a
    // backend-emitted failure is authoritative and takes priority.
    const isConnectionLost = !isFailure && streamStatus === "lost";

    const steps = buildSteps(slot?.plan ?? [], slot?.steps ?? [], isFailure);
    const currentIndex = steps.findIndex(step => step.status === "in_progress");
    const failedIndex = steps.findIndex(step => step.status === "failed");

    return {
      steps,
      totalSteps: steps.length,
      currentStep: currentIndex >= 0 ? steps[currentIndex] : undefined,
      currentIndex,
      failedIndex,
      failureReason: slot?.error?.reason,
      isFailure,
      isConnectionLost,
      hasAnyEvent: (slot?.steps?.length ?? 0) > 0
    };
  },
  { memoize: weakMapMemoize, argsMemoize: weakMapMemoize }
);

/**
 * Returns the `getProject` rejection error iff it was scoped to the same
 * `{organizationId, projectId}` the caller is rendering for. Prevents a stale
 * rejection from a previous route from triggering the error page on a fresh
 * mount.
 */
export const selectGetProjectError = (
  state: RootState,
  params: { organizationId: string; projectId: string }
) => {
  const err = state.organizationProjects.getProjectError;
  if (!err) return undefined;
  if (err.organizationId !== params.organizationId) return undefined;
  if (err.projectId !== params.projectId) return undefined;
  return err.error;
};

/**
 * Maximum age (in minutes) for a provisioning/failed project to remain visible
 * in the project list and count. Note: organizationProvisioningFailureProject
 * uses a separate 24-hour window (FAILED_PROJECT_TIME_LIMIT_HOURS) to keep the
 * failure card available longer in Redux — but the card and count are gated by
 * this shorter window via isWithinProvisioningStaleness.
 */
export const PROVISIONING_STALENESS_MINUTES = 15;

/** Returns true when a project was created within the staleness window. */
export const isWithinProvisioningStaleness = (createdAt: string) =>
  differenceInMinutes(new Date(createdAt), new Date()) >=
  -PROVISIONING_STALENESS_MINUTES;

export const organizationProvisioningProject = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (state, { organizationId }) =>
    Object.values(state.data?.[organizationId] ?? {})
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      .find(
        project =>
          project.status === OrganizationProjectStatusesTypes.requested &&
          isWithinProvisioningStaleness(project.created_at)
      )
);

// Time limit for showing failed provisioning projects (24 hours)
const FAILED_PROJECT_TIME_LIMIT_HOURS = 24;

export const organizationProvisioningFailureProject = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (state, { organizationId }) =>
    Object.values(state.data?.[organizationId] ?? {}).find(
      project =>
        project.status === OrganizationProjectStatusesTypes.failed &&
        differenceInHours(new Date(project.created_at), new Date()) >=
          -FAILED_PROJECT_TIME_LIMIT_HOURS
    )
);

/**
 * Returns true if the organization is currently transitioning from provisioning to loaded state.
 * This is used to prevent a flash of the "no projects" view during the transition.
 */
export const isProvisioningCompletingSelector = createSelector(
  organizationProjectsStateSelector,
  (_: RootState, params: { organizationId: string }) => params,
  (state, { organizationId }) =>
    state.provisioningCompleting.includes(organizationId)
);

/**
 * The project page renders a "shell" (NavBar + provisioning surface, no
 * SubNavBar) while the project is still provisioning, has failed, or has
 * flipped to `active` but the regional `loadOneProject` is in flight.
 *
 * Both `Page.tsx` and `NavBar/Container.tsx` need to know this. Centralized
 * here so they stay in sync.
 */
export const selectIsProjectInShell = (
  orgProject: OrganizationProject | undefined,
  hasRegionalProject: boolean
): boolean => {
  const isInProvisioningFlow =
    orgProject?.status === OrganizationProjectStatusesTypes.requested ||
    orgProject?.status === OrganizationProjectStatusesTypes.failed;
  const isAwaitingRegional =
    orgProject?.status === OrganizationProjectStatusesTypes.active &&
    !hasRegionalProject;
  return isInProvisioningFlow || isAwaitingRegional;
};
